import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { waLink, fmtDate, fmtDateTime, INDIAN_STATES } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle, Plus, Phone, Mail, MapPin, Briefcase, CheckCircle2, Clock, Pencil, Loader2 } from "lucide-react";

const splitName = (full) => {
  const parts = (full || "").trim().split(/\s+/);
  if (parts.length <= 1) return { first: parts[0] || "", last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
};

function validateCustomer(f) {
  if (!f.first_name.trim()) return "First name is required";
  if (!f.last_name.trim()) return "Surname is required";
  const email = f.email.trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
  if (!f.address.trim()) return "Address is required";
  if (!f.city.trim()) return "City is required";
  if (!f.state.trim()) return "State is required";
  if (!/^\d{6}$/.test(f.pincode.trim())) return "A valid 6-digit pincode is required";
  return null;
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setData(r.data)).catch(() => navigate("/customers"));
  }, [id, navigate]);

  const addressLine = useMemo(
    () => [data?.address, data?.city, data?.state, data?.pincode].filter(Boolean).join(", "),
    [data]
  );

  const openEdit = () => {
    const { first, last } = splitName(data.customer_name);
    setForm({
      first_name: first, last_name: last, whatsapp: data.whatsapp || "",
      email: data.email || "", address: data.address || "", city: data.city || "",
      state: data.state || "", pincode: data.pincode || "",
    });
    setEditOpen(true);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const saveEdit = async () => {
    const err = validateCustomer(form);
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const payload = {
        customer_name: `${form.first_name.trim()} ${form.last_name.trim()}`.trim(),
        whatsapp: form.whatsapp.trim(), email: form.email.trim(), address: form.address.trim(),
        city: form.city.trim(), state: form.state.trim(), pincode: form.pincode.trim(),
      };
      const res = await api.put(`/customers/${id}`, payload);
      setData((prev) => ({ ...prev, ...res.data }));
      setEditOpen(false);
      toast.success("Customer details updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update customer");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="customer-loading">Loading…</div>;

  const stats = [
    { label: "Total Jobs", value: data.total_jobs, icon: Briefcase },
    { label: "Completed", value: data.completed_jobs, icon: CheckCircle2 },
    { label: "Active", value: data.active_jobs, icon: Clock },
  ];

  return (
    <div className="max-w-4xl" data-testid="customer-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Customer Profile</p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mt-1" data-testid="customer-name">{data.customer_name}</h1>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={openEdit} data-testid="edit-customer-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          )}
          {waLink(data.whatsapp || data.mobile) && (
            <a href={waLink(data.whatsapp || data.mobile)} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="whatsapp-customer-button" className="text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-900">
                <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp Customer
              </Button>
            </a>
          )}
          {user?.role !== "operator" && (
            <Button onClick={() => navigate(`/new-job?customer_id=${data.id}`)} data-testid="new-job-for-customer-button" className="font-semibold">
              <Plus className="h-4 w-4 mr-1" /> New Job
            </Button>
          )}
        </div>
      </div>

      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {data.mobile}{data.whatsapp && data.whatsapp !== data.mobile ? ` (WA: ${data.whatsapp})` : ""}</p>
          {data.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {data.email}</p>}
          {(data.address || data.city) && (
            <p className="flex items-start gap-2 sm:col-span-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              {addressLine}</p>
          )}
          <p className="text-xs text-muted-foreground sm:col-span-2">Customer since {fmtDate(data.created_at)}</p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-3 mt-4">
        {stats.map((s) => (
          <Card key={s.label} data-testid={`customer-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-4 text-center">
              <p className="font-display text-2xl font-bold">{s.value}</p>
              <p className="text-xs text-muted-foreground font-medium">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <h2 className="text-base md:text-lg font-semibold mt-6 mb-3">Job History</h2>
      <div className="space-y-2" data-testid="customer-jobs-list">
        {data.jobs?.length ? data.jobs.map((j) => (
          <button key={j.id} onClick={() => navigate(`/jobs/${j.id}`)} data-testid={`customer-job-${j.id}`}
            className="w-full flex items-center gap-3 rounded-xl border bg-card px-4 py-3 hover:bg-accent/50 transition-colors text-left">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{j.job_number} · {j.article_name}</p>
              <p className="text-xs text-muted-foreground truncate">{(j.required_finishes || []).join(", ")} · {fmtDateTime(j.created_at)} · {j.photo_count} photos</p>
            </div>
            <StatusBadge status={j.status} />
          </button>
        )) : <p className="text-sm text-muted-foreground">No jobs yet for this customer.</p>}
      </div>

      {form && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-lg" data-testid="edit-customer-dialog">
            <DialogHeader>
              <DialogTitle>Edit Customer</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>First Name *</Label>
                <Input data-testid="edit-customer-name-input" value={form.first_name} onChange={(e) => setField("first_name", e.target.value)} className="h-11 mt-1" placeholder="First name" />
              </div>
              <div>
                <Label>Surname *</Label>
                <Input data-testid="edit-customer-surname-input" value={form.last_name} onChange={(e) => setField("last_name", e.target.value)} className="h-11 mt-1" placeholder="Surname" />
              </div>
              <div>
                <Label>WhatsApp Number</Label>
                <Input data-testid="edit-customer-whatsapp-input" inputMode="numeric" value={form.whatsapp} onChange={(e) => setField("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 15))} className="h-11 mt-1" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="edit-customer-email-input" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className="h-11 mt-1" placeholder="name@example.com (optional)" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address *</Label>
                <Input data-testid="edit-customer-address-input" value={form.address} onChange={(e) => setField("address", e.target.value)} className="h-11 mt-1" placeholder="House / building, street, area" />
              </div>
              <div>
                <Label>City *</Label>
                <Input data-testid="edit-customer-city-input" value={form.city} onChange={(e) => setField("city", e.target.value)} className="h-11 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>State *</Label>
                  <Select value={form.state} onValueChange={(v) => setField("state", v)}>
                    <SelectTrigger data-testid="edit-customer-state-input" className="h-11 mt-1"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pincode *</Label>
                  <Input data-testid="edit-customer-pincode-input" inputMode="numeric" value={form.pincode} onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-11 mt-1" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)} data-testid="edit-customer-cancel">Cancel</Button>
              <Button onClick={saveEdit} disabled={saving} className="font-semibold" data-testid="edit-customer-save">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
